import React, { useEffect, useState } from 'react';
import { Briefcase, Handshake, ShieldCheck, Activity, CalendarCheck, Users, FileText, Building2, ExternalLink } from 'lucide-react';
import { PORTAL_URL } from '../SiteLayout';
import PageSEO from '../../components/PageSEO';

const WHY_PARTNER = [
  'Une équipe de professionnels expérimentés',
  'Une prise en charge rapide et coordonnée',
  'Un interlocuteur dédié pour les partenaires',
  'Des prestations conformes à la réglementation congolaise',
  'Des outils numériques facilitant le suivi administratif et médical',
  'Une capacité d\u2019intervention sur plusieurs sites',
];

const SOLUTIONS = [
  { title: 'Conventions médicales', text: "Prise en charge des collaborateurs selon les modalités définies avec l'entreprise." },
  { title: 'Consultations spécialisées', text: "Accès facilité aux différentes spécialités du Centre." },
  { title: 'Hospitalisation', text: "Prise en charge des collaborateurs nécessitant des soins hospitaliers." },
  { title: 'Examens de laboratoire', text: "Bilans biologiques de routine ou spécialisés." },
  { title: 'Imagerie médicale', text: "Échographie, mammographie (à terme) et autres examens diagnostiques." },
  { title: 'Téléconsultation', text: "Accès aux médecins du Centre à distance lorsque cela est approprié." },
  { title: 'Assistance médicale', text: "Accompagnement des collaborateurs nécessitant un suivi particulier." },
];

const WORK_MED = [
  "Visites médicales d'embauche",
  'Visites médicales périodiques',
  'Visites de reprise et de préreprise',
  "Visites à la demande de l'employeur ou du salarié",
  "Certificats d'aptitude",
  'Évaluation des risques professionnels',
  'Conseils en prévention',
];

const PREVENTION = [
  'Campagnes de dépistage',
  'Vaccination',
  'Sensibilisation aux maladies chroniques',
  'Santé de la femme et santé maternelle',
  'Nutrition',
  'Santé mentale',
  'Prévention des addictions',
  'Sécurité routière',
  'Premiers secours',
];

const FAQS = [
  { q: 'Les conventions sont-elles personnalisées ?', a: 'Oui. Chaque convention est élaborée en fonction des besoins de l\u2019entreprise.' },
  { q: 'Intervenez-vous directement dans les entreprises ?', a: 'Oui. Certaines prestations peuvent être organisées sur site selon les besoins.' },
  { q: 'Pouvez-vous assurer le suivi de plusieurs sites ?', a: "Oui. Notre organisation nous permet d'accompagner des entreprises disposant de plusieurs implantations." },
  { q: 'Les données médicales sont-elles confidentielles ?', a: "Oui. Toutes les informations médicales sont protégées par le secret médical et ne sont communiquées qu'aux personnes autorisées, conformément aux obligations légales et déontologiques." },
];

export default function SitePartners() {
  const [partners, setPartners] = useState({ assurances: [], conventionnes: [] });

  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${API}/api/site/partners`)
      .then(r => (r.ok ? r.json() : { assurances: [], conventionnes: [] }))
      .then(setPartners)
      .catch(() => setPartners({ assurances: [], conventionnes: [] }));
  }, []);

  const renderPartnerGrid = (list) => {
    const API = process.env.REACT_APP_BACKEND_URL || '';
    return (
      <div className="site-partner-logos-grid">
        {list.map((p) => (
          <a
            key={p.partner_id || p.id}
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            className="site-partner-logo-card"
            data-testid={`partner-logo-${p.partner_id || p.id}`}
            title={`Visiter ${p.name}`}
          >
            <div className="site-partner-logo-wrap">
              <img
                src={p.logo_url?.startsWith('http') ? p.logo_url : `${API}${p.logo_url}`}
                alt={`Logo ${p.name}`}
                loading="lazy"
              />
            </div>
            <span className="site-partner-logo-name">
              {p.name} <ExternalLink size={13} style={{ opacity: 0.55 }} />
            </span>
          </a>
        ))}
      </div>
    );
  };

  return (
    <main data-testid="site-partners">
      <PageSEO
        title="Entreprises & Partenaires — Conventions et Assurances"
        description="Le CMT signe des conventions avec entreprises et assureurs (SUNU, RAWSUR, MSO, GGA, CNSS, ACGT, Facility Services) pour la santé de vos collaborateurs à Matadi et Boma."
        path="/entreprises"
      />
      <div className="site-page-head">
        <div className="site-page-head-inner">
          <h1 className="site-reveal">Un partenaire de confiance pour la santé de vos collaborateurs</h1>
          <p className="site-reveal-1">
            Le Centre Médical la Trinité accompagne les entreprises et les institutions dans la mise en œuvre
            de solutions de santé adaptées à leurs besoins. Grâce à une approche personnalisée, une équipe
            pluridisciplinaire et des outils numériques innovants, nous contribuons à améliorer le bien-être
            des travailleurs, à réduire les risques professionnels et à faciliter l&apos;accès aux soins.
          </p>
          <div style={{ display: 'flex', gap: 10, marginTop: '1.5rem', flexWrap: 'wrap' }}>
            <a href="/contact" className="site-btn site-btn-primary" data-testid="partners-cta-convention-btn">
              <Handshake size={18} /> Demander une convention
            </a>
            <a href="/contact" className="site-btn site-btn-outline" style={{ background: 'white' }}>
              Parler à un conseiller
            </a>
          </div>
        </div>
      </div>

      {/* Pourquoi CMT */}
      <section className="site-section">
        <span className="site-kicker">Pourquoi choisir le CMT ?</span>
        <h2>Pourquoi faire confiance au Centre Médical la Trinité ?</h2>
        <ul style={{ marginTop: '1.5rem', columnCount: 2, columnGap: '2.5rem', listStyle: 'none', padding: 0 }}>
          {WHY_PARTNER.map((w, i) => (
            <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: '.85rem', breakInside: 'avoid' }}>
              <ShieldCheck size={18} color="var(--site-primary)" style={{ flexShrink: 0, marginTop: 3 }} />
              <span>{w}</span>
            </li>
          ))}
        </ul>
        <p style={{ marginTop: '1rem', color: 'var(--site-muted)' }}>
          Notre objectif est de devenir un partenaire durable contribuant à la performance des organisations par une meilleure santé au travail.
        </p>
      </section>

      {/* Solutions */}
      <div className="site-section-alt-wrap">
        <section className="site-section">
          <span className="site-kicker">Nos solutions</span>
          <h2>Des services adaptés à votre organisation</h2>
          <div className="site-grid" style={{ marginTop: '2rem' }}>
            {SOLUTIONS.map((s, i) => (
              <div className="site-card" key={i}>
                <div className="site-card-icon"><Briefcase size={22} /></div>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Médecine du travail + Prévention */}
      <section className="site-section">
        <div className="site-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))' }}>
          <div className="site-card" style={{ textAlign: 'left' }}>
            <div className="site-card-icon"><FileText size={24} /></div>
            <h3>Médecine du travail</h3>
            <p style={{ color: 'var(--site-muted)', marginBottom: '1rem' }}>
              Nous accompagnons les employeurs dans le respect des obligations réglementaires en matière de santé au travail.
            </p>
            <ul style={{ paddingLeft: '1.2rem', color: 'var(--site-muted)', fontSize: 14, lineHeight: 1.7 }}>
              {WORK_MED.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
            <p style={{ marginTop: '.75rem', fontSize: 13, fontStyle: 'italic', color: 'var(--site-muted)' }}>
              Toutes les évaluations sont réalisées dans le respect du secret médical et de l&apos;indépendance professionnelle des médecins.
            </p>
          </div>
          <div className="site-card" style={{ textAlign: 'left' }}>
            <div className="site-card-icon"><Activity size={24} /></div>
            <h3>Prévention et promotion de la santé</h3>
            <p style={{ color: 'var(--site-muted)', marginBottom: '1rem' }}>
              Nous accompagnons les entreprises dans la mise en œuvre d&apos;actions de prévention visant à améliorer durablement la santé des collaborateurs.
            </p>
            <ul style={{ paddingLeft: '1.2rem', color: 'var(--site-muted)', fontSize: 14, lineHeight: 1.7 }}>
              {PREVENTION.map((p, i) => <li key={i}>{p}</li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* Nos partenaires — logos réels */}
      {(partners.assurances.length > 0 || partners.conventionnes.length > 0) && (
        <div className="site-section-alt-wrap">
          <section className="site-section">
            <span className="site-kicker">Nos partenaires de confiance</span>
            <h2>Ils nous accordent leur confiance</h2>
            <p style={{ color: 'var(--site-muted)', maxWidth: 780, marginTop: '.5rem' }}>
              Le Centre Médical la Trinité collabore avec un réseau national de compagnies
              d&apos;assurance et d&apos;organismes conventionnés pour faciliter la prise en charge
              des collaborateurs de leurs assurés et affiliés.
            </p>

            {partners.assurances.length > 0 && (
              <div style={{ marginTop: '2rem' }}>
                <h3 style={{ fontSize: '1.15rem', color: 'var(--site-dark)', marginBottom: '1rem' }}>
                  Compagnies d&apos;assurance partenaires
                </h3>
                {renderPartnerGrid(partners.assurances)}
              </div>
            )}

            {partners.conventionnes.length > 0 && (
              <div style={{ marginTop: '2.5rem' }}>
                <h3 style={{ fontSize: '1.15rem', color: 'var(--site-dark)', marginBottom: '1rem' }}>
                  Organismes conventionnés
                </h3>
                {renderPartnerGrid(partners.conventionnes)}
              </div>
            )}

            <p style={{ fontSize: '.8rem', color: 'var(--site-muted)', marginTop: '2rem', fontStyle: 'italic', textAlign: 'center' }}>
              Votre société ou organisme n&apos;apparaît pas dans cette liste ?{' '}
              <a href="/contact" style={{ color: 'var(--site-primary)', fontWeight: 600 }}>Contactez-nous</a>{' '}
              pour mettre en place une convention.
            </p>
          </section>
        </div>
      )}

      {/* Numérique */}
      <div className="site-section-alt-wrap">
        <section className="site-section">
          <div style={{ maxWidth: 900, margin: '0 auto', textAlign: 'center' }}>
            <span className="site-kicker">Gestion numérique</span>
            <h2>Une collaboration plus fluide grâce au numérique</h2>
            <p style={{ color: 'var(--site-muted)', lineHeight: 1.75 }}>
              Grâce à <strong>CMT Health Platform</strong>, les entreprises partenaires bénéficient progressivement
              de services numériques facilitant la gestion administrative et médicale de leurs collaborateurs :
              demande de rendez-vous, suivi des visites médicales, transmission sécurisée des certificats d&apos;aptitude,
              accès aux rapports d&apos;activité consolidés et échanges sécurisés avec le Centre. Cette digitalisation
              vise à améliorer la réactivité, la traçabilité et la qualité du suivi.
            </p>
          </div>
        </section>
      </div>

      {/* Démarche */}
      <section className="site-section">
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <span className="site-kicker">Notre démarche de partenariat</span>
          <h2>Construisons une collaboration durable</h2>
          <p style={{ color: 'var(--site-muted)', lineHeight: 1.75 }}>
            Chaque partenariat débute par une analyse des besoins de l&apos;organisation. Nos équipes élaborent
            ensuite une proposition adaptée à votre secteur d&apos;activité, à vos effectifs et aux risques
            spécifiques auxquels vos collaborateurs sont exposés. Nous privilégions une relation de proximité,
            fondée sur l&apos;écoute, la transparence et l&apos;amélioration continue de la qualité des services.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <div className="site-section-alt-wrap">
        <section className="site-section">
          <span className="site-kicker">Questions fréquentes</span>
          <h2>Vos questions, nos réponses</h2>
          <div style={{ marginTop: '1.5rem', display: 'grid', gap: 10 }}>
            {FAQS.map((f, i) => (
              <details key={i} style={{ padding: '1rem 1.25rem', background: '#fff', borderRadius: 10, border: '1px solid #e2e8f0' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600 }}>{f.q}</summary>
                <p style={{ marginTop: '.5rem', color: 'var(--site-muted)' }}>{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      </div>

      {/* CTA */}
      <section className="site-section">
        <div className="site-cta-band">
          <div>
            <h2>Construisons ensemble un environnement de travail plus sain</h2>
            <p>Convention médicale, visites, prévention ou accompagnement personnalisé : nos équipes sont à votre disposition.</p>
          </div>
          <a href="/contact" className="site-btn site-btn-outline" style={{ background: 'white' }} data-testid="partners-cta-contact-btn">
            <Building2 size={18} /> Demander une convention
          </a>
        </div>
      </section>
    </main>
  );
}
